import { useQuery } from "@tanstack/react-query";
import {
  fetchCategories,
  fetchBrands,
  fetchSubCategories,
  fetchMails,
} from "../api/homeApi";

/**
 * Declarative data-fetching hook for the WebHome page using TanStack Query.
 *
 * Replaces the brittle 4-step imperative payload chain:
 *   setCatPayload → [categoryList] effect → setBrandPayload
 *   → [isBrandLoading] effect → setSubCatPayload
 *   → [subCategoryList] effect → setMailPayload
 *
 * Each query is gated behind `enabled` flags that enforce the same
 * dependency order. Query keys include all filter state so that any
 * filter change (category, brand, subcat, location, seen) automatically
 * triggers a fresh fetch without manual payload assembly.
 *
 * Background polling keeps the UI eventually-consistent with the database.
 */
export const useHomeQueries = ({
  selCity,
  selState,
  selCountry,
  selCounty,
  selCategory,
  selBrand,
  subCatActive,
  seen,
  userDetails,
}) => {
  const token = userDetails?.token;
  const user_uuid = userDetails?.uuid;
  const locationReady = !!selCountry && !!user_uuid;

  // ── User location from profile ─────────────────────────────────────────────
  // Stored as [longitude, latitude] to match GeoJSON / MongoDB convention.
  const userCoords = userDetails?.location?.coordinates;
  const [userLng, userLat] = userCoords?.length === 2 ? userCoords : [];

  // ── 1. Categories ──────────────────────────────────────────────────────────
  // Backend returns categories sorted by mails_count desc when city_uuid is
  // provided — first entry = most relevant category for this city.
  const { data: categoryData, isLoading: isCategoryLoading } = useQuery({
    queryKey: ["home-categories", { city_uuid: selCity, user_uuid }],
    queryFn: () => fetchCategories({ city_uuid: selCity, user_uuid, token }),
    enabled: locationReady && !!selCity,
    staleTime: 5_000,
    refetchInterval: 10_000,
  });
  const categoryList = categoryData ?? [];

  // ── 2. Brands for selected category in this city ───────────────────────────
  const { data: brandData, isLoading: isBrandLoading } = useQuery({
    queryKey: [
      "home-brands",
      {
        category_uuid: selCategory,
        country_uuid: selCountry,
        state_uuid: selState,
        city_uuid: selCity,
        county_uuid: selCounty,
        user_uuid,
        user_lng: userLng,
        user_lat: userLat,
      },
    ],
    queryFn: () =>
      fetchBrands({
        category_uuid: selCategory,
        country_uuid: selCountry,
        state_uuid: selState,
        city_uuid: selCity,
        county_uuid: selCounty,
        user_uuid,
        user_lng: userLng,
        user_lat: userLat,
        token,
      }),
    enabled: locationReady && !!selCategory,
    staleTime: 5_000,
    refetchInterval: 10_000,
  });
  // Backend returns stores sorted by proximity when coords are provided.
  // Falls back to mails_count order when no coords.
  const barndList = brandData?.data ?? [];

  // For the mail query: when no brand is explicitly selected, fall back to all
  // brands in the carousel so mails are scoped to city stores only.
  // For the subcat query: omit brand_uuid when empty so the backend returns
  // city-wide unread counts (matching the old useSubCategory hook behaviour).
  const effectiveBrands =
    selBrand?.length > 0 ? selBrand : barndList.map((b) => b.uuid);

  // ── 3. Sub-categories (waits for brands to avoid empty barndList race) ──────
  // When selBrand is empty we omit brand_uuid entirely so the backend returns
  // city-wide counts — matching the behaviour of the old useSubCategory hook.
  const { data: subcatData, isLoading: isSubCatLoading } = useQuery({
    queryKey: [
      "home-subcats",
      {
        category_uuid: selCategory,
        country_uuid: selCountry,
        state_uuid: selState,
        city_uuid: selCity,
        county_uuid: selCounty,
        brands: selBrand?.join(",") ?? "",
        user_uuid,
      },
    ],
    queryFn: () =>
      fetchSubCategories({
        category_uuid: selCategory,
        country_uuid: selCountry,
        state_uuid: selState,
        city_uuid: selCity,
        county_uuid: selCounty,
        user_uuid,
        brand_uuid: selBrand,
        token,
      }),
    enabled: locationReady && !!selCategory && !isBrandLoading,
    staleTime: 5_000,
    refetchInterval: 10_000,
    select: (data) =>
      [...data].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
  });
  const subCategoryList = subcatData ?? [];

  // ── 4. Mails ────────────────────────────────────────────────────────────────
  const mailQueryParams =
    locationReady && !!selCategory && !!subCatActive
      ? `country_uuid=${selCountry}&state_uuid=${selState ?? ""}&city_uuid=${selCity ?? ""}&user_uuid=${user_uuid}&county_uuid=${selCounty ?? ""}` +
        `&category_uuid=${selCategory}` +
        (effectiveBrands.length > 0 ? `&brand_uuid=${effectiveBrands}` : "") +
        `&sub_category_uuid=${subCatActive}&type=filter&search=&pagenum=&limit=&seen=${seen ?? ""}`
      : null;

  const { data: mailData, isLoading: isMailLoading } = useQuery({
    queryKey: [
      "home-mails",
      {
        country: selCountry,
        state: selState,
        city: selCity,
        county: selCounty,
        category: selCategory,
        brands: effectiveBrands.join(","),
        subCat: subCatActive,
        seen,
        user_uuid,
      },
    ],
    queryFn: () => fetchMails({ params: mailQueryParams, token }),
    enabled: !!mailQueryParams && !isSubCatLoading,
    staleTime: 5_000,
    refetchInterval: 10_000,
  });
  const mailCount = mailData?.count ?? 0;
  const mailList = mailData?.data ?? [];

  return {
    categoryList,
    isCategoryLoading,
    barndList,
    brandCount: brandData?.count ?? 0,
    isBrandLoading,
    subCategoryList,
    isSubCatLoading,
    mailList,
    mailCount,
    isMailLoading,
  };
};
